import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ICONS from "../../../../assets/Icons";
import {
  addBeneficiaryAccountLabel,
  removeBeneficiaryAccountLabel,
  resetBeneficiaryAccountLabel,
} from "../../../../redux/features/accounts/beneficiaryAccount/index.js";
import {
  addInternalAccountLabel,
  removeInternalAccountLabel,
} from "../../../../redux/features/accounts/internalAccount/index.js";
import {
  ownerAccountsAddLabel,
  ownerAccountsRemoveLabel,
} from "../../../../redux/features/owner/ownerAllVerification/ownerAllVerification";

import {
  addNewLabelRequest,
  removeNewLabelRequest,
} from "../../../../redux/features/reimbursementRequest/index";
import {
  addReimbursementTxnLabel,
  removeReimbursementTxnLabel,
} from "../../../../redux/features/reimbursementTransaction";
import {
  beneficiaryTransactionsAddLabel,
  beneficiaryTransactionsRemoveLabel,
} from "../../../../redux/features/transactions/beneficiaryTransactions/index.js";
import {
  internalTransactionsAddLabel,
  internalTransactionsRemoveLabel,
} from "../../../../redux/features/transactions/internalTransactions/index.js";
import {
  GlobalConfiguration,
  IntervalConfiguration,
} from "../../../../utils/configurationProvider";
import { linkIdentifier } from "../../../../utils/linkIdentifier";
import IconRoundButton from "../../CommonButtons/IconRoundButton";
import PrimaryWithIconButton from "../../CommonButtons/PrimaryWithIconButton";
import SearchBarWithCTA from "../../CommonSearchComponents/SearchBarWithCTA";
import CommonToast from "../../CommonToast";
import CommonCountMultiSelect from "../../NumberCountComponent/CommonCountMultiSelect";

// Suggestions of props for component while using the components -------------------------------------
/**
 * @typedef {((e: React.MouseEvent<HTMLButtonElement, MouseEvent>?) => void)} OnClickEventHandler
 */
/**
 * @typedef {("button" | "submit")} BtnType
 */
/**
 * @typedef {("white" | "black")} HoverOption
 */
/**
 * @param {Object} props
 * @param {string} props.headerTitle - Main title text to display at the top of popup
 * @param {string} [props.headersubtext] - Subtitle/description text below headers
 * @param {string} [props.closeenabledBgColor] - Background color when close button is enabled (CSS variable)
 * @param {boolean} [props.closeisLoading] - Show loading state on close button (true/false)
 * @param {OnClickEventHandler} [props.closehandleFunction] - Handle function when close button is clicked
 * @param {BtnType} [props.closebtnType] - Button type for close button eg:- "button" or "submit"
 * @param {boolean} [props.closeisDisabled] - Disable close button (true/false)
 * @param {string} [props.closedisabledBgColor] - Background color when close button is disabled (CSS variable)
 * @param {React.RefObject} [props.closebuttonRef] - Button ref for close button (optional)
 * @param {string} [props.closeimgColor] - Icon color for close button (CSS variable)
 * @param {string} [props.ctabtnText] - Text for CTA button
 * @param {string} [props.ctatxtColor] - Text color for CTA button
 * @param {string} [props.ctaplaceholder] - Placeholder for search input
 * @param {string} [props.labelsubtext] - Subtext for labels section
 * @param {string} [props.nolablefound] - Message when no labels found
 * @param {Array} props.labels - Array of available labels
 * @param {Function} props.setLabels - Function to update labels array
 * @param {string|Array} props.LabelAccounts - Account ID(s) to assign labels to
 * @param {boolean} props.isLabelPopupOpen - Whether popup is open
 * @param {Array} [props.filteredData] - Filtered account data
 * @param {string} [props.type] - Type of label operation: "create", "beneTxn", "internalTxn", "owner", "internal"
 * @param {React.RefObject} [props.labelActionDispatchedRef] - Ref to track if label action was dispatched
 * @param {Function} [props.setSelectedLabelsCreate] - Function to set selected labels for create type
 * @param {Array} [props.selectedLabelsCreate] - Selected labels for create type
 * @param {Array} [props.txnPayLoad] - Transaction payload for labels
 * @param {OnClickEventHandler} [props.onClose] - Handle function when popup is closed
 * @returns {JSX.Element}
 */
const CommonLabelPopup = ({
  headerTitle,
  headersubtext,
  closeenabledBgColor,
  closeisLoading,
  closehandleFunction,
  closebtnType,
  closeisDisabled,
  closedisabledBgColor,
  closebuttonRef,
  closeimgColor,
  ctabtnText,
  ctaplaceholder,
  labelsubtext,
  nolablefound,
  labels = [],
  setLabels,
  LabelAccounts,
  isLabelPopupOpen,
  filteredData,
  type,
  labelActionDispatchedRef,
  setSelectedLabelsCreate,
  selectedLabelsCreate = [],
  txnPayLoad,
  onClose,
}) => {
  const labelInputRef = useRef(null);
  const dispatch = useDispatch();
  const prevSelectedRef = useRef([]);

  const { addLabelsBeneAccountLoading, removeLabelsBeneAccountLoading } =
    useSelector((state) => state?.beneficiaryAccount);
  const {
    addLabelsInternalAccountLoading,
    removeLabelsInternalAccountLoading,
  } = useSelector((state) => state?.beneficiaryAccount);

  const { addLabelsPvtLoading, removeLabelsPvtLoading } = useSelector(
    (state) => state?.privatetransactions,
  );

  const [animate, setAnimate] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState([]);
  const [labelSearchName, setLabelSearchName] = useState("");
  const autoSelectRef = useRef(null);

  const handleInputChange = (value) => {
    // SearchField passes value as string, not event object
    // Remove uppercase letters and spaces, limit to 50 characters
    const processedValue =
      value?.replace(/[A-Z\s]/g, "")?.substring(0, 50) || "";
    setLabelSearchName(processedValue);
  };

  const handleNewLabel = () => {
    const trimmed = labelSearchName.trim();
    if (!trimmed) return;
    const labelExists = labels.includes(trimmed);
    if (!labelExists) {
      const updatedLabels = [...labels, trimmed];
      setLabels(updatedLabels);
      if (type === "create") {
        sessionStorage.setItem(
          "beneficiaryTxnLabels",
          JSON.stringify(updatedLabels),
        );
        sessionStorage.setItem(
          "beneficiaryAccountLabels",
          JSON.stringify(updatedLabels),
        );
        sessionStorage.setItem(
          "internalTxnLabels",
          JSON.stringify(updatedLabels),
        );

        sessionStorage.setItem(
          "AvailableLabelsPvt",
          JSON.stringify(updatedLabels),
        );

        sessionStorage.setItem(
          "AvailableLabelsRequest",
          JSON.stringify(updatedLabels),
        );
      }
      autoSelectRef.current = trimmed;
      setLabelSearchName("");
    }
  };

  useEffect(() => {
    if (autoSelectRef.current) {
      const newLabel = autoSelectRef.current;
      if (labels.includes(newLabel)) {
        if (type === "create") {
          if (
            setSelectedLabelsCreate &&
            !selectedLabelsCreate.includes(newLabel)
          ) {
            setSelectedLabelsCreate((prev) => [...prev, newLabel]);
          }
        } else {
          if (!selectedLabel.includes(newLabel)) {
            setSelectedLabel((prev) => [...prev, newLabel]);
          }
        }
        autoSelectRef.current = null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labels, type, selectedLabelsCreate, selectedLabel]);

  const trimmedInput = labelSearchName?.trim();
  const labelExists = labels?.some(
    (label) => label?.toLowerCase() === trimmedInput?.toLowerCase(),
  );

  const isButtonDisabled = trimmedInput === "" || labelExists;

  // Add keydown handler and maxLength using useEffect to attach to input element
  useEffect(() => {
    const inputElement = labelInputRef?.current?.querySelector("input");
    if (inputElement) {
      const handleKeyDown = (e) => {
        if (e.key === "Enter" && !isButtonDisabled) {
          e.preventDefault();
          handleNewLabel();
        }
      };
      inputElement.addEventListener("keydown", handleKeyDown);
      // Set maxLength attribute directly
      inputElement.setAttribute("maxLength", "50");

      return () => {
        inputElement.removeEventListener("keydown", handleKeyDown);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isButtonDisabled, labelSearchName]);

  const isLabelActionLoading =
    addLabelsBeneAccountLoading ||
    removeLabelsBeneAccountLoading ||
    removeLabelsInternalAccountLoading ||
    addLabelsInternalAccountLoading ||
    addLabelsPvtLoading ||
    removeLabelsPvtLoading;

  const handleLabelClick = (label) => {
    if (type === "create") {
      setSelectedLabelsCreate((prev) => {
        if (prev?.includes(label)) {
          return prev?.filter((l) => l !== label);
        } else {
          return [...prev, label];
        }
      });
    } else {
      setSelectedLabel((prev) => {
        if (prev?.includes(label)) {
          return prev?.filter((l) => l !== label);
        } else {
          return [...prev, label];
        }
      });
    }
  };

  useEffect(() => {
    if (isLabelPopupOpen) {
      dispatch?.(resetBeneficiaryAccountLabel());
    }
  }, [isLabelPopupOpen, dispatch]);

  const handleSelectedLabels = () => {
    const prevSelected = prevSelectedRef?.current || [];
    const currentSelected = selectedLabel || [];
    const added = currentSelected?.filter(
      (label) => !prevSelected?.includes(label),
    );
    const removed = prevSelected?.filter(
      (label) => !currentSelected?.includes(label),
    );

    const showToast = (message) => {
      CommonToast({
        text: message,
        icon: ICONS?.LOADER_GIF,
        variant: "var(--processing)",
        buttonNeeded: false,
        btnProps: {
          enabledBgColor: "var(--primary-bg)",
          enabledTextColor: "var(--text-primary)",
          btnText: "",
          handleFunction: () => {},
          icon: "",
          btnType: "button",
          isDisabled: false,
          hoverOption: "white",
        },
      });
    };
    const toArray = (value) => (Array?.isArray(value) ? value : [value]);

    if (added?.length > 0) {
      showToast("Assigning label");
      if (type !== "create") {
        if (labelActionDispatchedRef) {
          labelActionDispatchedRef.current = true;
        }

        if (type === "beneTxn") {
          dispatch(
            beneficiaryTransactionsAddLabel({
              record_ids: toArray(LabelAccounts),
              labels: added,
            }),
          );
        } else if (type === "requestReimbursementTxn") {
          dispatch(
            addNewLabelRequest({
              record_ids: toArray(LabelAccounts),
              labels: added,
            }),
          );
        } else if (type === "ReimburseTxn") {
          dispatch(
            addReimbursementTxnLabel({
              record_ids: toArray(LabelAccounts),
              labels: added,
            }),
          );
        } else if (type === "ReimburseTxn") {
          dispatch(
            addReimbursementTxnLabel({
              record_ids: toArray(LabelAccounts),
              labels: added,
            }),
          );
        } else if (type === "internalTxn") {
          dispatch(
            internalTransactionsAddLabel({
              record_ids: toArray(LabelAccounts),
              labels: added,
            }),
          );
        } else {
          if (type === "owner") {
            dispatch(
              ownerAccountsAddLabel({
                record_ids: toArray(LabelAccounts),
                labels: added,
              }),
            );
          } else if (type === "internal") {
            dispatch(
              addInternalAccountLabel({
                record_ids: toArray(LabelAccounts),
                labels: added,
              }),
            );
          } else {
            dispatch(
              addBeneficiaryAccountLabel({
                record_ids: toArray(LabelAccounts),
                labels: added,
              }),
            );
          }
        }
      }
    }

    if (removed?.length > 0) {
      showToast("Unassigning label");
      if (type !== "create") {
        if (labelActionDispatchedRef) {
          labelActionDispatchedRef.current = true;
        }

        if (type === "beneTxn") {
          dispatch(
            beneficiaryTransactionsRemoveLabel({
              record_ids: toArray(LabelAccounts),
              labels: removed,
            }),
          );
        } else if (type === "requestReimbursementTxn") {
          dispatch(
            removeNewLabelRequest({
              record_ids: toArray(LabelAccounts),
              labels: removed,
            }),
          );
        } else if (type === "ReimburseTxn") {
          dispatch(
            removeReimbursementTxnLabel({
              record_ids: toArray(LabelAccounts),
              labels: removed,
            }),
          );
        } else if (type === "ReimburseTxn") {
          dispatch(
            removeReimbursementTxnLabel({
              record_ids: toArray(LabelAccounts),
              labels: removed,
            }),
          );
        } else if (type === "internalTxn") {
          dispatch(
            internalTransactionsRemoveLabel({
              record_ids: toArray(LabelAccounts),
              labels: removed,
            }),
          );
        } else {
          if (type === "owner") {
            dispatch(
              ownerAccountsRemoveLabel({
                record_ids: toArray(LabelAccounts),
                labels: removed,
              }),
            );
          } else if (type === "internal") {
            dispatch(
              removeInternalAccountLabel({
                record_ids: toArray(LabelAccounts),
                labels: removed,
              }),
            );
          } else {
            dispatch(
              removeBeneficiaryAccountLabel({
                record_ids: toArray(LabelAccounts),
                labels: removed,
              }),
            );
          }
        }
      }
    }

    prevSelectedRef.current = currentSelected;
  };

  useEffect(() => {
    handleSelectedLabels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLabel]);

  useEffect(() => {
    if (!!LabelAccounts?.length && !selectedLabel.length) {
      const selectedAccounts = Array.isArray(filteredData)
        ? filteredData.filter((item) =>
            LabelAccounts?.includes(
              type === "beneTxn" ||
                type === "internalTxn" ||
                type === "ReimburseTxn"
                ? item?.txn_id
                : type === "pvtTxn"
                  ? item?.request_reference_id
                  : type === "requestReimbursementTxn"
                    ? item?.request_id
                    : item?.id,
            ),
          )
        : [];

      const labelSets = selectedAccounts?.map(
        (item) => new Set(item?.labels || []),
      );

      let commonLabels = [];

      if (labelSets?.length > 0) {
        commonLabels = [
          ...labelSets?.reduce(
            (a, b) => new Set([...a]?.filter((label) => b?.has(label))),
          ),
        ];
      }

      setSelectedLabel?.(commonLabels);
      prevSelectedRef.current = commonLabels;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [LabelAccounts]);

  useEffect(() => {
    if (type === "create" && txnPayLoad?.length) {
      const newLabels = txnPayLoad;

      const newSelected = Array.isArray(newLabels)
        ? newLabels.filter((label) => !selectedLabelsCreate?.includes(label))
        : [];
      if (newSelected?.length && setSelectedLabelsCreate) {
        setSelectedLabelsCreate((prev) => [...(prev || []), ...newSelected]);
      }

      const newLabelToAdd = Array.isArray(newLabels)
        ? newLabels?.filter((label) => !labels?.includes(label))
        : [];
      if (newLabelToAdd?.length && setLabels) {
        setLabels((prev) => [...(prev || []), ...newLabelToAdd]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, txnPayLoad]);

  const filteredLabels = labels?.filter((label) =>
    label?.toLowerCase()?.includes(labelSearchName?.toLowerCase()),
  );

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else if (closehandleFunction) {
      closehandleFunction();
    }
  };

  const assignedCount =
    type === "create"
      ? selectedLabelsCreate?.length || GlobalConfiguration?.lengthCheckZero
      : selectedLabel?.length || GlobalConfiguration?.lengthCheckZero;

  // Same condition as original: show labels only if both labels and filteredLabels have items
  const hasLabels =
    labels?.length !== GlobalConfiguration?.lengthCheckZero &&
    filteredLabels?.length !== GlobalConfiguration?.lengthCheckZero;

  const toSentenceCase = (text = "") =>
    text.charAt(GlobalConfiguration?.lengthCheckZero).toUpperCase() +
    text.slice(GlobalConfiguration?.lengthCheckOne).toLowerCase();

  const needsFullStop = (text = "") => !text.trim().endsWith(".");

  return (
    <div
      className="flex w-full justify-center items-center m-auto top-1/2 left-1/2 fixed z-z-index-modal p-padding-sm"
      style={{ transform: "translate(-50%, -50%)" }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className={` h-fit  w-full   h-label_popup_heights overflow-hidden relative Qmd:w-small-popup-width flex flex-col pt-padding-md bg-primary-bg rounded-border-radius-md animate-none ${animate ? "Qmd:animate-slideInFromLeft" : "Qmd:animate-slideInFromRight"}`}
      >
        <div className="flex flex-col flex-1">
          <div className=" flex flex-col gap-gap-xs px-padding-md">
            <div className="flex justify-between gap-gap-md ">
              <div className=" flex gap-gap-sm">
                <div className="flex flex-col gap-gap-xs">
                  <p className="text-font-md text-text-primary font-semibold">
                    {toSentenceCase(headerTitle)}
                  </p>
                  {headersubtext && (
                    <span className="font-normal text-text-secondary text-font-sm">
                      {linkIdentifier(headersubtext)}
                      {needsFullStop(headersubtext) && <span>.</span>}
                    </span>
                  )}
                </div>
              </div>
              <IconRoundButton
                enabledBgColor={closeenabledBgColor}
                isLoading={closeisLoading}
                handleFunction={() => {
                  if (!closeisDisabled) {
                    setAnimate(true);
                    setTimeout(() => {
                      handleClose();
                    }, IntervalConfiguration.intervalSemiMid);
                  }
                }}
                title="Close"
                icon={ICONS?.CLOSE}
                hoverOption="white"
                btnType={closebtnType}
                isDisabled={closeisDisabled}
                disabledBgColor={closedisabledBgColor}
                buttonRef={closebuttonRef}
                imgColor={closeimgColor}
              />
            </div>
          </div>

          <div className=" pt-padding-md px-padding-md ">
            <SearchBarWithCTA
              btnText={ctabtnText}
              placeholder={ctaplaceholder}
              searchText={labelSearchName}
              inActiveVariantNeeded={true}
              onInput={handleInputChange}
              onClick={handleNewLabel}
              disabledBtnColor="var(--text-disabled)"
              disabledTxtColor="var(--text-primary)"
              disabledBgColor="var(--text-disabled)"
              disabledTextColor="var(--text-primary)"
              txtColor="var(--text-primary)"
              isBtnDisable={isButtonDisabled}
              searchRef={labelInputRef}
              isSearchDisabled={false}
              btnColor="var(--positive-act)"
              hoverOption="black"
            />
          </div>
          <div className="w-full gap-gap-xs flex flex-col  pt-padding-md px-padding-md">
            {labelsubtext && (
              <span className="text-text-secondary text-font-xs font-normal">
                {labels?.length === 0
                  ? "To assign a label, type a new label name in the search bar and click 'Assign new label'."
                  : filteredLabels?.length === 0
                    ? "Click on 'Assign new label' to assign the label entered above."
                    : labelsubtext}
              </span>
            )}
          </div>
          <div className="w-full  pt-padding-sm pl-padding-md flex-1 overflow-y-hidden h-full">
            {hasLabels ? (
              <div className="flex gap-gap-sm flex-wrap overflow-y-auto pr-padding-md pb-padding-sm max-h-labelpopup-button-height-web Qsmd:max-h-labelpopup-button-height-web">
                {filteredLabels?.map((label, index) => {
                  const isSelected =
                    selectedLabel?.includes(label) ||
                    selectedLabelsCreate?.includes(label);
                  const isDisabled = isLabelActionLoading && !isSelected;

                  return (
                    <PrimaryWithIconButton
                      key={index}
                      btnText={label}
                      handleFunction={() => {
                        if (!isDisabled) handleLabelClick(label);
                      }}
                      enabledBgColor={
                        isSelected
                          ? "var(--Category-selection)"
                          : "var(--primary-nav-act)"
                      }
                      enabledTextColor="var(--text-primary)"
                      isDisabled={isDisabled}
                      icon={
                        isSelected
                          ? ICONS?.SELECTED_WITH_GREEN_CIRCLE
                          : ICONS?.NOT_SELECTED_WITH_GREY_CIRCLE
                      }
                      iconToBeAlign="right"
                      hoverOption={isSelected ? "black" : "white"}
                      btnType="button"
                      fullWidthRequired={false}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col justify-center items-center gap-gap-sm py-padding-md flex-1  h-full">
                <img
                  className="h-graphic-square-height-sm"
                  src={ICONS?.ASSIGN}
                  alt="nolablefound"
                />
                <p className="text-text-secondary text-font-xs font-normal">
                  {nolablefound}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="w-full flex justify-center flex-col bg-primary-card p-padding-md rounded-b-border-radius-md">
          <div className="flex gap-gap-sm items-center">
            <CommonCountMultiSelect
              bgColor={"bg-primary-bg"}
              textColor={"text-text-primary"}
              count={assignedCount}
            />
            <p className="text-text-primary text-font-md font-normal">
              Labels assigned
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommonLabelPopup;
